import { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { NAV_SECTIONS, ALWAYS_ITEMS } from '@/lib/navigation';
import { useCrumbLabels } from '@/lib/breadcrumbs';
import { paths } from '@/routes/paths';

/**
 * Breadcrumbs derived from the route and the navigation tree, rather than written by
 * hand on each page — hand-maintained crumbs drift from the routes they describe.
 */

const EXTRA_LABELS: Record<string, string> = {
  new: 'New',
  edit: 'Edit',
  apply: 'Apply',
  board: 'Board',
  team: 'Team',
  calendar: 'Calendar',
  company: 'Company',
  roles: 'Roles',
  'design-system': 'Design system',
  time: 'Time & leave',
  attendance: 'My attendance',
  holidays: 'Holidays',
  'leave-types': 'Leave types',
  documents: 'Documents',
  onboarding: 'Onboarding',
  employees: 'Employees',
  leave: 'My leave',
  organization: 'Organization',
};

function labelForPath(path: string): string | null {
  const item = [...NAV_SECTIONS.flatMap((s) => s.items), ...ALWAYS_ITEMS].find(
    (i) => i.to === path,
  );
  return item?.label ?? null;
}

function titleCase(segment: string): string {
  return segment.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** A 24-character hex id. Shown as "Record" until the screen registers a real name. */
function isObjectId(segment: string): boolean {
  return /^[0-9a-f]{24}$/i.test(segment);
}

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const registered = useCrumbLabels();
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  const crumbs = segments.map((segment, index) => {
    const to = `/${segments.slice(0, index + 1).join('/')}`;
    const label =
      labelForPath(to) ??
      // A record's own name, registered by the screen that loaded it.
      registered(segment) ??
      EXTRA_LABELS[segment] ??
      (isObjectId(segment) ? 'Record' : titleCase(segment));
    return { to, label, last: index === segments.length - 1 };
  });

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex items-center gap-1 overflow-hidden text-caption text-fg-muted">
        <li className="shrink-0">
          <Link to={paths.dashboard} className="inline-flex min-h-6 items-center hover:text-fg">
            Home
          </Link>
        </li>
        {crumbs.map((crumb) => (
          <Fragment key={crumb.to}>
            <li aria-hidden className="shrink-0">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="min-w-0 truncate">
              {crumb.last ? (
                <span aria-current="page" className="font-medium text-fg">
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.to} className="inline-flex min-h-6 items-center hover:text-fg">
                  {crumb.label}
                </Link>
              )}
            </li>
          </Fragment>
        ))}
      </ol>
    </nav>
  );
}
